/**
 * Intelligence Agent registry.
 *
 * Single source of truth for the six "intelligence" agents (Fracture,
 * Compass, Keystone, Meridian, Condition Watch, Beacon). The generic report
 * page, the generic settings page, and the Agents hub all read from here, so
 * adding another such agent is one entry plus an analyzer + a pair of routes.
 */

import type { LucideIcon } from 'lucide-react';
import type { BatchStats, RunScope } from '../agentRunService';
import type { AgentSettings } from '../agentSettingsService';
import { BEACON_AGENT, CASSANDRA_AGENT, COMPASS_AGENT, KEYSTONE_AGENT, MERIDIAN_AGENT, CONDITION_WATCH_AGENT, REVIEW_DUE_AGENT, type AgentDefinition } from '../ai/agentDefinitions';
import { runCassandraBatch, CASSANDRA_AGENT_TYPE } from './cassandraAnalyzer';
import { runCompassBatch, COMPASS_AGENT_TYPE } from './compassAnalyzer';
import { runKeystoneBatch, KEYSTONE_AGENT_TYPE } from './keystoneAnalyzer';
import { runMeridianBatch, MERIDIAN_AGENT_TYPE } from './meridianAnalyzer';
import { runConditionWatchBatch, CONDITION_WATCH_AGENT_TYPE } from './conditionWatchAnalyzer';
import { runBeaconBatch, BEACON_AGENT_TYPE } from './beaconAnalyzer';
import { runReviewDueBatch, REVIEW_DUE_AGENT_TYPE } from './reviewDueAnalyzer';

export type IntelligenceBatchRunner = (
  workspaceId: string,
  scope: RunScope,
  config: AgentSettings,
  onProgress: (done: number, total: number) => void,
  signal: AbortSignal,
) => Promise<BatchStats>;

export interface IntelligenceAgentMeta {
  agentType: string;
  name: string;
  /** Short one-liner. */
  tagline: string;
  /** Paragraph for the Agents hub + settings header. */
  description: string;
  icon: LucideIcon;
  /** Tailwind accent classes. */
  accent: { iconBg: string; iconText: string; chip: string };
  reportHref: string;
  settingsHref: string;
  batchRunner: IntelligenceBatchRunner;
  /** Human labels for each finding_type this agent emits. */
  findingTypeLabels: Record<string, string>;
  /** Subtitle shown on the report page when findings exist. */
  reportSubtitle: string;
  /** Empty-state copy on the report page. */
  emptyHeadline: string;
  emptyBody: string;
  /** "How it works" bullets on the settings page. */
  howItWorks: string[];
  /** Default system prompt shown as placeholder. */
  defaultSystemPrompt: string;
}

/** The words and addresses of an agent on the template come from its definition — one copy. */
function fromDefinition(def: AgentDefinition): Pick<IntelligenceAgentMeta, 'name' | 'tagline' | 'description' | 'icon' | 'accent' | 'reportHref' | 'settingsHref' | 'howItWorks' | 'defaultSystemPrompt'> {
  return {
    icon: def.icon,
    accent: def.accent,
    name: def.name,
    tagline: def.tagline,
    description: def.description,
    reportHref: def.reportHref,
    settingsHref: def.settingsHref,
    howItWorks: def.howItWorks,
    defaultSystemPrompt: def.defaultSystemPrompt,
  };
}

export const CASSANDRA: IntelligenceAgentMeta = {
  ...fromDefinition(CASSANDRA_AGENT),
  agentType: CASSANDRA_AGENT_TYPE,
  batchRunner: runCassandraBatch,
  findingTypeLabels: {
    knife_edge: 'Knife-edge',
    lone_criterion: 'Single-criterion',
    hidden_dissent: 'Hidden dissent',
  },
  reportSubtitle: 'Fragility and dissent detected in your scored rankings',
  emptyHeadline: 'No fragility detected',
  emptyBody:
    'Run Fracture after a scoring session. It analyses the per-criterion, per-participant scores and flags rankings that could flip, lean on one criterion, or hide strong opposition.',
};

export const COMPASS: IntelligenceAgentMeta = {
  ...fromDefinition(COMPASS_AGENT),
  agentType: COMPASS_AGENT_TYPE,
  batchRunner: runCompassBatch,
  findingTypeLabels: {
    orphaned_objective: 'Orphaned objective',
    under_owned: 'Under-owned',
    unbacked_priorities: 'Unbacked work',
    portfolio_imbalance: 'Imbalance',
  },
  reportSubtitle: 'Gaps between your stated strategy and your real priorities',
  emptyHeadline: 'Strategy and priorities are aligned',
  emptyBody:
    'Run Compass to compare your strategic objectives against what your priorities actually serve. It needs at least one strategic objective with priority mappings to find gaps.',
};

export const KEYSTONE: IntelligenceAgentMeta = {
  ...fromDefinition(KEYSTONE_AGENT),
  agentType: KEYSTONE_AGENT_TYPE,
  batchRunner: runKeystoneBatch,
  findingTypeLabels: {
    keystone: 'Keystone',
    dependency_cycle: 'Dependency loop',
  },
  reportSubtitle: 'High-leverage items and impossible loops in your dependency graph',
  emptyHeadline: 'No keystones detected',
  emptyBody:
    'Run Keystone once your items have dependency relationships (use Nexus to map them). It computes transitive leverage and flags the items that quietly gate many others, plus any circular dependencies.',
};

export const MERIDIAN: IntelligenceAgentMeta = {
  ...fromDefinition(MERIDIAN_AGENT),
  agentType: MERIDIAN_AGENT_TYPE,
  batchRunner: runMeridianBatch,
  findingTypeLabels: {
    starved_priority: 'Starved priority',
    attention_sink: 'Attention sink',
  },
  reportSubtitle: 'Where execution activity has drifted from the ratified ranking',
  emptyHeadline: 'Commitment and attention are aligned',
  emptyBody:
    'Run Meridian once a priority list is ratified and a connector (Jira, Linear, GitHub) is linked to your items. It compares each item\'s share of the last 30 days of connector activity against its rank — and stays silent when there is too little signal to judge.',
};

export const CONDITION_WATCH: IntelligenceAgentMeta = {
  ...fromDefinition(CONDITION_WATCH_AGENT),
  agentType: CONDITION_WATCH_AGENT_TYPE,
  batchRunner: runConditionWatchBatch,
  findingTypeLabels: {
    condition_b_candidate: 'Signal not acted on',
    condition_c_candidate: 'Change without signal',
  },
  reportSubtitle: 'Candidate governance gaps for human review — questions, never verdicts or conditions',
  emptyHeadline: 'No condition candidates detected',
  emptyBody:
    'Run Condition Watch once signals (agent findings, impact events, stakeholder pulses) and governance acts (rescorings, reprioritization triggers, catchball, ratifications) have accumulated. It flags material signals nobody acted on and plan changes with no signal behind them — as questions for the cycle owner, never as verdicts. It never sets a condition; that stays with a human via set_por_condition.',
};

export const BEACON: IntelligenceAgentMeta = {
  ...fromDefinition(BEACON_AGENT),
  agentType: BEACON_AGENT_TYPE,
  batchRunner: runBeaconBatch,
  findingTypeLabels: {
    external_signal: 'External signal',
  },
  reportSubtitle: 'External signals material to your ratified plan — each with its evidence link',
  emptyHeadline: 'No material external signals',
  emptyBody:
    'Beacon runs server-side on the Ambient Runtime schedule (or Run now). It derives a watchlist from your ratified priorities, strategies, and plan assumptions, sweeps external news for those entities, and stays silent unless a signal touches something you already committed to. Silence means nothing it saw was material — not that nothing happened.',
};

export const REVIEW_DUE: IntelligenceAgentMeta = {
  ...fromDefinition(REVIEW_DUE_AGENT),
  agentType: REVIEW_DUE_AGENT_TYPE,
  batchRunner: runReviewDueBatch,
  findingTypeLabels: {
    decision_review_due: 'Decision review due',
    term_needs_decision: 'Term awaiting decision',
    term_review_overdue: 'Term past review date',
    signal_unanswered: 'Signal unanswered',
    condition_threshold_breached: 'Condition past its bound',
    condition_evaluation_stale: 'Condition not checked recently',
  },
  reportSubtitle: 'What the record says is due for reconsideration — proposals only, deciding stays with you',
  emptyHeadline: 'Nothing is due for reconsideration',
  emptyBody:
    'Review Due runs server-side on the Ambient Runtime schedule (or Run now). It scans decision review dates, open commitment terms, and unanswered signals, and proposes a candidate when a clock has passed. An empty docket means every recorded review date is still in the future and every tripped term has been resolved.',
};

export const INTELLIGENCE_AGENTS: Record<string, IntelligenceAgentMeta> = {
  [CASSANDRA.agentType]: CASSANDRA,
  [COMPASS.agentType]: COMPASS,
  [KEYSTONE.agentType]: KEYSTONE,
  [MERIDIAN.agentType]: MERIDIAN,
  [CONDITION_WATCH.agentType]: CONDITION_WATCH,
  [BEACON.agentType]: BEACON,
  [REVIEW_DUE.agentType]: REVIEW_DUE,
};

export const INTELLIGENCE_AGENT_LIST: IntelligenceAgentMeta[] = [CASSANDRA, COMPASS, KEYSTONE, MERIDIAN, CONDITION_WATCH, BEACON, REVIEW_DUE];
